import React, { useState, useContext, useEffect } from 'react';
import { Form, Button, Card, Alert, Container, Row, Col } from 'react-bootstrap';
import AuthContext from '../../context/AuthContext';

const ProfilePage = () => {
  const { user, updateProfile, error, setError } = useContext(AuthContext);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [validated, setValidated] = useState(false);
  const [passwordMatch, setPasswordMatch] = useState(true);
  const [successMessage, setSuccessMessage] = useState('');
  
  const { username, email, password, confirmPassword } = formData;
  
  // Load user data
  useEffect(() => {
    if (user) {
      setFormData({
        username: user.username || '',
        email: user.email || '',
        password: '',
        confirmPassword: ''
      });
    }
    
    return () => {
      // Clear any errors when component unmounts
      if (setError) setError(null);
    };
  }, [user, setError]);
  
  const onChange = e => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    
    // Check password match
    if (e.target.name === 'confirmPassword' || e.target.name === 'password') {
      if (e.target.name === 'confirmPassword') {
        setPasswordMatch(e.target.value === password || e.target.value === '');
      } else {
        setPasswordMatch(confirmPassword === e.target.value || confirmPassword === '');
      }
    }
    
    // Clear success message on change
    if (successMessage) {
      setSuccessMessage('');
    }
  };
  
  const onSubmit = async e => {
    e.preventDefault();
    
    // Clear previous messages
    setSuccessMessage('');
    if (setError) setError(null);
    
    // Check passwords match if password is being changed
    if (password && password !== confirmPassword) {
      setPasswordMatch(false);
      return;
    }
    
    const form = e.currentTarget;
    if (form.checkValidity() === false) {
      e.stopPropagation();
      setValidated(true);
      return;
    }
    
    // Only include password if it's being changed
    const userData = {
      username,
      email
    };
    
    if (password) {
      userData.password = password;
    }
    
    const success = await updateProfile(userData);
    if (success) {
      setSuccessMessage('Profile updated successfully');
      // Clear password fields
      setFormData({
        ...formData,
        password: '',
        confirmPassword: ''
      });
    }
  };
  
  if (!user) {
    return <div>Loading...</div>;
  }
  
  return (
    <Container className="my-4">
      <Row className="justify-content-center">
        <Col md={8}>
          <Card className="shadow-sm">
            <Card.Body>
              <h2 className="text-center mb-4">Your Profile</h2>
              
              {error && <Alert variant="danger">{error}</Alert>}
              {successMessage && <Alert variant="success">{successMessage}</Alert>}
              
              <Form noValidate validated={validated} onSubmit={onSubmit}>
                <Form.Group className="mb-3" controlId="username">
                  <Form.Label>Username</Form.Label>
                  <Form.Control
                    type="text"
                    name="username"
                    value={username}
                    onChange={onChange}
                    required
                  />
                  <Form.Control.Feedback type="invalid">
                    Please provide a valid username.
                  </Form.Control.Feedback>
                </Form.Group>
                
                <Form.Group className="mb-3" controlId="email">
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={email}
                    onChange={onChange}
                    required
                  />
                  <Form.Control.Feedback type="invalid">
                    Please provide a valid email address.
                  </Form.Control.Feedback>
                </Form.Group>
                
                <Form.Group className="mb-3" controlId="role">
                  <Form.Label>Role</Form.Label>
                  <Form.Control
                    type="text"
                    value={user.role}
                    readOnly
                    disabled
                  />
                </Form.Group>
                
                <hr />
                <h5>Change Password</h5>
                <p className="text-muted">Leave blank to keep current password</p>
                
                <Form.Group className="mb-3" controlId="password">
                  <Form.Label>New Password</Form.Label>
                  <Form.Control
                    type="password"
                    name="password"
                    value={password}
                    onChange={onChange}
                    minLength={6}
                  />
                  <Form.Control.Feedback type="invalid">
                    Password must be at least 6 characters.
                  </Form.Control.Feedback>
                </Form.Group>
                
                <Form.Group className="mb-3" controlId="confirmPassword">
                  <Form.Label>Confirm New Password</Form.Label>
                  <Form.Control
                    type="password"
                    name="confirmPassword"
                    value={confirmPassword}
                    onChange={onChange}
                    isInvalid={!passwordMatch}
                  />
                  <Form.Control.Feedback type="invalid">
                    Passwords do not match.
                  </Form.Control.Feedback>
                </Form.Group>
                
                <div className="d-grid gap-2">
                  <Button variant="primary" type="submit">
                    Update Profile
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default ProfilePage; 